// apInvoicesApi.ts — the ONE place every vendor-invoice API-shape assumption lives.
//
// ERP expansion chunk 2, phase 4 (frontend). The backend
// (api/src/procurement/vendor-invoices-router.js + vendor-invoices-service.js)
// shipped first; the shapes below are read directly off that code, not guessed.
// If the contract shifts, this file is the only place that changes.
//
// Money is integer cents from the API — EXCEPT unit_price_cents, which is a
// NUMERIC string and may carry fractional cents ("208.318"): Digikey prices
// passives that way and the digits matter. formatCents handles both; nothing
// outside this file divides by 100.
//
// A credit memo's amounts and totals arrive NEGATIVE (the 054 sign convention,
// applied once at read time); unit prices stay positive. Render the sign, never
// flip it.
//
// Access: every route sits behind the STRICT financial gate (can_financial,
// not admin-bypassed). Nav and routes gate the same way; a 403 here means the
// person genuinely lacks the flag, not a bug.

import { useApi } from '../apiHelpers';

// ---------------------------------------------------------------------------
// Enums, verbatim from the CHECK constraints (054/055)
// ---------------------------------------------------------------------------

export type InvoiceStatus =
  | 'needs_review'
  | 'confirmed'
  | 'delivery_failed'
  | 'delivered'
  | 'discarded'
  | 'void';

export type HeaderMatchMethod = 'po_ref' | 'amount_single' | 'manual' | null;

export type LineMatchMethod = 'part_number' | 'unit_price' | 'description' | 'manual' | null;

export type InvoiceLineType = 'stock' | 'service' | 'charge';

/** The bookkeeper's note (064): where payment stands. HUMAN-SET — the actual
 *  payments live in QuickBooks, so her word is the source. Overdue is NOT a
 *  member: it is derived (isOverdue below), never stored. */
export type PaymentStatus = 'open' | 'partial' | 'paid';

export type ChargeType = 'freight' | 'duty' | 'tariff' | 'tax' | 'brokerage' | 'other' | null;

export interface AttentionReason {
  code: string;
  message: string;
}

/**
 * WHY billing runs ahead of receiving, when it does. `ahead_of_receiving` says
 * that it does; this says which of the two very different things happened, and
 * they want opposite reactions from a bookkeeper:
 *
 * - `return_pending`: the goods arrived and went back. Nothing is wrong with
 *   the invoice, the vendor owes a credit or replacement units, and the gap
 *   closes itself when they answer. Neutral, not a warning.
 * - `over_billed`: the invoice bills more than ever showed up. That is the
 *   amber one, and the one somebody has to act on.
 *
 * null whenever `ahead_of_receiving` is false.
 */
export type AheadReason = 'return_pending' | 'over_billed' | null;

// ---------------------------------------------------------------------------
// Row shapes
// ---------------------------------------------------------------------------

/** One row of GET /procurement/vendor-invoices (the queue). */
export interface InvoiceQueueRow {
  id: string;
  vendor_id: string | null;
  vendor_name: string | null;
  vendor_observed: string | null;
  invoice_number: string | null;
  invoice_date: string | null;        // YYYY-MM-DD — format with formatEventDate
  due_date: string | null;
  is_credit_memo: boolean;
  total_cents: string | null;         // bigint arrives as a string
  currency: string;
  status: InvoiceStatus;
  match_method: HeaderMatchMethod;
  needs_attention: boolean;
  attention_reasons: AttentionReason[] | null;
  likely_duplicate_of: string | null;
  /** Where the document came from. `manual` is a credit note somebody TYPED
   *  from a purchase return (072): there is no file behind it, and the file
   *  route 404s, so surfaces that show the paper have to branch on this. */
  source: 'email' | 'upload' | 'manual';
  original_filename: string | null;
  created_at: string;                 // timestamptz
  line_count: number;
  matched_line_count: number;
  /** The confirm-time send decision (061). With delivered_at null and status
   *  'confirmed', a send is OWED — visible, never silently fine. */
  delivery_intended: boolean;
  delivered_at: string | null;
  delivery_error: string | null;
  /** The claim (062): set with no delivered_at and status 'confirmed' means an
   *  attempt whose outcome is unknown — check QuickBooks before retrying. */
  delivery_attempted_at: string | null;
}

/** The full header, GET /procurement/vendor-invoices/:id. */
export interface VendorInvoice extends Omit<InvoiceQueueRow, 'line_count' | 'matched_line_count'> {
  subtotal_cents: string | null;
  tax_cents: string | null;
  ai_confidence: string | null;
  ai_remarks: string | null;
  discard_reason: string | null;
  notes: string | null;
  mime_type: string | null;
  delivered_via: string | null;
  delivery_ref: string | null;
  delivered_document: 'original' | 'summary' | null;
  payment_status: PaymentStatus;
  payment_status_by: string | null;
  payment_status_at: string | null;
  payment_on_hold: boolean;
  payment_hold_reason: string | null;
  confirmed_at: string | null;
  voided_at: string | null;
  /** Read since phase 2, stored since 075 — QuickBooks has a Terms field. */
  payment_terms: string | null;
  /** The address THIS document printed, shaped like the vendors columns.
   *  Observed, not master: it wins on the summary page. */
  vendor_address_observed: ObservedAddress | null;
  /** What a human picked. Neither gates confirm: QuickBooks cannot receive
   *  either through an emailed document, so they are read, not ingested.
   *
   *  The two id types differ and that is not a typo: purchase_classes.id is
   *  int4 and arrives as a NUMBER, expense_categories.id is int8 and arrives as
   *  a STRING, per this module's bigint-crosses-as-a-string rule. Compare them
   *  loosely or through String(), never with === across the two. */
  purchase_class_id: number | null;
  expense_category_id: string | null;
  /** What THIS document named, print-only (076). */
  parties_observed: ObservedParties | null;
  /** Where the vendor asked to be paid. The bank fields reach THIS screen and
   *  deliberately not the forwarded page: QuickBooks reads four things off an
   *  emailed document and remittance is not among them, so an account number
   *  there would sit in a third party's store for no functional gain. */
  remit_to_observed: ObservedRemitTo | null;
  order_source: string | null;
  /** Does the VENDOR MASTER already have an address? Decides whether the copy
   *  is offered at all: a button whose only outcome is a refusal is worse than
   *  no button. */
  vendor_has_address: boolean | null;
}

/** Mirrors the vendors columns exactly, which is what makes adopting it a
 *  field-for-field move with nothing to reinterpret. */
export interface ObservedAddress {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

/** One party the document named: a person, a company, an address, any subset. */
export interface ObservedParty {
  name?: string | null;
  attention?: string | null;
  address?: ObservedAddress | null;
}

export interface ObservedParties {
  bill_to?: ObservedParty | null;
  ship_to?: ObservedParty | null;
  ship_from?: ObservedParty | null;
  buyer?: ObservedParty | null;
}

export interface ObservedRemitTo {
  name?: string | null;
  address?: ObservedAddress | null;
  bank_name?: string | null;
  account_number?: string | null;
  routing_number?: string | null;
  swift?: string | null;
}

/** A party as a block of lines: the person, the company, then the address. */
export function partyLines(party: ObservedParty | null | undefined): string[] {
  if (!party) return [];
  return [party.attention, party.name, ...addressLines(party.address)]
    .map((l) => (l || '').trim())
    .filter(Boolean);
}

export interface PurchaseClassOption { id: number; key: string; label: string }
export interface ExpenseCategoryOption {
  /** int8: a string, unlike PurchaseClassOption.id. See VendorInvoice. */
  id: string;
  key: string;
  label: string;
  qbo_account_ref: string | null;
  qbo_account_name: string | null;
}
export interface ClassificationOptions {
  classes: PurchaseClassOption[];
  categories: ExpenseCategoryOption[];
}

/** The address as a person reads it, one line per element. Shared by the panel
 *  and anything else that prints one. */
export function addressLines(address: ObservedAddress | null | undefined): string[] {
  if (!address) return [];
  const cityLine = [address.city, address.region].filter(Boolean).join(', ');
  return [
    address.line1,
    address.line2,
    [cityLine, address.postal_code].filter(Boolean).join(' '),
    address.country,
  ].map((l) => (l || '').trim()).filter(Boolean);
}

// ---------------------------------------------------------------------------
// The PO page's billing read, GET /procurement/vendor-invoices/for-order/:id
// ---------------------------------------------------------------------------

/** One invoice as the PO page sees it: the invoice's own facts plus this
 *  order's slice of it. `counted` is the rollup predicate (confirmed and not
 *  void) — a review row's amounts are a claim, a void row's are history. */
export interface OrderInvoice {
  id: string;
  vendor_id: string | null;
  vendor_name: string | null;
  vendor_observed: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  due_date: string | null;
  /** The order's payment clock applied to THIS invoice's date, when that clock
   *  counts from the invoice (migration 078). Null for an order-anchored clock
   *  (that due date is the PO header's) and when the invoice has no date. The
   *  printed due_date wins over it; see effectiveDueDate. */
  terms_due_date: string | null;
  is_credit_memo: boolean;
  currency: string;
  total_cents: number | null;
  status: InvoiceStatus;
  payment_status: PaymentStatus;
  payment_status_at: string | null;
  payment_on_hold: boolean;
  payment_hold_reason: string | null;
  needs_attention: boolean;
  attention_reasons: AttentionReason[] | null;
  confirmed_at: string | null;
  voided_at: string | null;
  delivered_at: string | null;
  delivery_error: string | null;
  created_at: string;
  /** What this invoice bills ON THIS ORDER — the column that sums per order.
   *  The invoice's own total is one column over, and the two differ exactly
   *  when other_po_numbers is non-empty. */
  this_order_cents: number;
  counted: boolean;
  other_po_numbers: string[];
  /** The third leg of the three-way match: billing runs ahead of what has
   *  physically arrived (returns counted, stock lines only). Informational,
   *  never blocking — invoice-before-goods is normal, PAYING before goods is
   *  the decision this flag exists to inform. */
  ahead_of_receiving: boolean;
  /** Which kind of ahead. See AheadReason: a return in progress is not a
   *  problem with the invoice, and must not wear the same chip as one that
   *  bills for goods that never came. */
  ahead_reason: AheadReason;
}

/** One PO line's three-way numbers plus the invoices that bill it. */
export interface OrderLineBilling {
  purchase_order_line_id: string;
  line_number: number;
  line_type: InvoiceLineType;
  billed_amount_cents: number;
  net_received: number;
  line_value_cents: number;
  ahead_of_receiving: boolean;
  /** Which kind of ahead, per line. Same two states as the invoice's. */
  ahead_reason: AheadReason;
  invoices: Array<{
    vendor_invoice_id: string;
    invoice_number: string | null;
    amount_cents: number | null;
    counted: boolean;
  }>;
}

export interface OrderBilling {
  invoices: OrderInvoice[];
  lines: OrderLineBilling[];
  percent_billed: number;
}

export interface VendorInvoiceLine {
  id: string;
  vendor_invoice_id: string;
  line_number: number;
  description: string | null;
  part_number_observed: string | null;
  mfg_part_number_observed: string | null;
  quantity: string | null;            // NUMERIC as string, fractions legal
  unit_price_cents: string | null;    // NUMERIC as string, sub-cent digits legal
  amount_cents: string | null;        // bigint as string; negative on credits
  line_type: InvoiceLineType;
  charge_type: ChargeType;
  purchase_order_line_id: string | null;
  match_method: LineMatchMethod;
  /** Seeded from the header by "apply to all", editable per line: a bill can
   *  legitimately split goods and freight across accounts. */
  purchase_class_id: number | null;
  expense_category_id: string | null;
  /** The order line this bills, with its three-way numbers. null (or absent,
   *  on an older API) for an unmatched line or a charge with no order line. */
  po_context?: LinePoContext | null;
}

/** What a matched line's order says, beside what this invoice bills on it.
 *  Every cents field crosses as a NUMBER here, unlike the bigint strings on
 *  the line itself. The two ids are uuids, so strings. */
export interface LinePoContext {
  purchase_order_id: string;
  purchase_order_line_id: string;
  po_number: string;
  po_line_number: number;
  /** Catalog display name, else sku. */
  item_name: string | null;
  line_type: string;
  quantity_ordered: number | null;
  /** Received minus refunded. */
  net_received: number;
  pending_return_quantity: number;
  /** The ORDER price per unit. */
  unit_cost_cents: number | null;
  /** Ordered times the order price. */
  line_value_cents: number;
  /** Other confirmed invoices on this order line. */
  billed_before_cents: number;
  /** Every line of THIS invoice on this order line. */
  this_invoice_cents: number;
  /** line_value - billed_before - this_invoice, floored at 0. */
  remaining_after_cents: number;
  verdict: LineVerdict;
}

export interface LineVerdict {
  /** Stock lines only. */
  ahead_of_receiving: boolean;
  ahead_reason: AheadReason;
  /** 0 when not ahead. */
  beyond_cents: number;
  /** beyond_cents at the order price; null when the order has no price. */
  beyond_units: number | null;
  exceeds_remaining: boolean;
  /** 0 when not. */
  exceeds_by_cents: number;
  invoice_unit_price_cents: number | null;
  /** Invoice price minus order price; null when equal or unknown. */
  price_variance_cents: number | null;
}

export interface InvoiceEdit {
  id: number;
  field: string;
  old_value: string | null;
  new_value: string | null;
  edited_by: string | null;
  created_at: string;
}

export interface InvoiceDetail {
  invoice: VendorInvoice;
  lines: VendorInvoiceLine[];
  edits: InvoiceEdit[];
  /** Where a confirmed invoice's summary forwards — printed beside the send
   *  toggle so the destination is never a surprise. */
  qbo_forward_address: string;
  /** Whether this environment can actually send. False until QuickBooks is
   *  ready to accept bills, and the toggle's copy changes to say so: leaving it
   *  ON queues the forward for the first sweep, and switching it OFF is the one
   *  decision the sweep will never undo. */
  qbo_delivery_enabled: boolean;
}

/** POST /:id/deliver — exactly one of these shapes comes back. */
export interface DeliverResult {
  delivered?: VendorInvoice;
  skipped?: 'delivery_disabled' | 'mail_not_configured';
  failed?: VendorInvoice | null;
  /** The mail went but the record could not say so: outcome unknown,
   *  check QuickBooks before any retry. */
  stamp_failed?: boolean;
  error_message?: string;
}

// ---------------------------------------------------------------------------
// Rematch (proposals; writes nothing)
// ---------------------------------------------------------------------------

export interface MatchCandidate {
  purchase_order_line_id: string;
  purchase_order_id: string;
  po_number: string | null;
  line_number: number;
  line_type: InvoiceLineType;
  part_catalog_id: number | null;
  sku: string | null;
  part_description: string | null;
  /** The catalog row's name, joined in. Render with components/ItemName. */
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
  unit_cost_cents: number | null;
  quantity_expected: number | null;
  net_received: number;
  remaining_to_bill_cents: number;
  billable: boolean;
}

export interface RematchLine {
  line_number: number;
  description: string | null;
  line_type: InvoiceLineType;
  purchase_order_line_id: string | null;   // the machine's proposal
  match_method: LineMatchMethod;
  match_reason: string | null;             // ambiguous_part | ambiguous_price | exceeds_remaining | line_already_billed
  match_score?: number | null;
  bills_beyond_received?: boolean;
  candidates: MatchCandidate[];
  current_match: {                          // the human's stored pick, separately
    purchase_order_line_id: string | null;
    match_method: LineMatchMethod;
  };
}

export interface RematchResult {
  vendor: { id: string; name: string; resolution?: string } | null;
  match_method: HeaderMatchMethod;
  orders: Array<{ id: string; po_number: string; status: string; vendor_id: string | null }>;
  unmatched_po_keys: string[];
  vendor_mismatch: boolean | null;
  lines: RematchLine[];
}

// ---------------------------------------------------------------------------
// Payment-note vocabulary and derivations
// ---------------------------------------------------------------------------

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  open: 'Open',
  partial: 'Partial',
  paid: 'Paid',
};

/** The fields overdue is derived from. Both OrderInvoice and VendorInvoice
 *  satisfy it structurally. */
export interface OverdueSubject {
  due_date: string | null;
  /** Billing-rail rows only; the queue's rows have no order clock. */
  terms_due_date?: string | null;
  payment_status: PaymentStatus;
  is_credit_memo: boolean;
  confirmed_at: string | null;
  voided_at: string | null;
}

/** Overdue is DERIVED, here and NOWHERE else — the whole predicate, not just
 *  the calendar half. Only a bill can be overdue: confirmed (the rollup
 *  predicate, not status), not void, not a credit memo (money the vendor owes
 *  US has no payment deadline of ours), not marked paid. The date comparison
 *  is lexicographic YYYY-MM-DD against the local calendar date — no Date
 *  parse, no timezone shift. Splitting the payability half across call sites
 *  is exactly how a review row once rendered a red overdue date. */
/** The date to SHOW for a bill: what the vendor printed, else what the order's
 *  payment clock computes from the invoice date. Display only: overdue stays
 *  on the printed date, because the AP queue and the invoice page carry no
 *  order clock and one bill must not read overdue on one page and not the
 *  other. The billing rail labels a derived date "(from terms)". */
export function effectiveDueDate(inv: Pick<OverdueSubject, 'due_date' | 'terms_due_date'>): string | null {
  return inv.due_date || inv.terms_due_date || null;
}

export function isOverdue(inv: OverdueSubject): boolean {
  if (!inv.due_date || inv.payment_status === 'paid') return false;
  if (inv.is_credit_memo || !inv.confirmed_at || inv.voided_at) return false;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return inv.due_date < today;
}

// ---------------------------------------------------------------------------
// Formatting — the one cents-to-dollars seam
// ---------------------------------------------------------------------------

/** "$17,609.47", "-$161.86", em-dash placeholder for null. Handles bigint strings
 *  and fractional-cent NUMERIC strings alike. */
export function formatCents(cents: string | number | null | undefined): string {
  if (cents === null || cents === undefined || cents === '') return '—';
  const n = typeof cents === 'string' ? Number(cents) : cents;
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}$${(Math.abs(n) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Cents → a bare dollar NUMBER, for Export CSV. The sibling of formatCents and
 *  here for the same reason: this module is the only place AP cents become
 *  dollars. A spreadsheet wants a number it can sum, so there is no currency
 *  sign and no fixed decimals, and an absent figure stays null rather than
 *  becoming a zero somebody would then add up. A credit memo keeps its negative
 *  sign, which is how a credit reads in a ledger. */
export function centsToDollars(cents: string | number | null | undefined): number | null {
  if (cents === null || cents === undefined || cents === '') return null;
  const n = typeof cents === 'string' ? Number(cents) : cents;
  return Number.isFinite(n) ? n / 100 : null;
}

/** A unit price with its sub-cent digits intact: "208.318¢" → "$2.08318". */
export function formatUnitPrice(centsText: string | null | undefined): string {
  if (!centsText) return '—';
  const n = Number(centsText);
  if (!Number.isFinite(n)) return '—';
  const dollars = n / 100;
  const decimals = Math.max(2, (centsText.split('.')[1] || '').length + 2);
  return `$${dollars.toFixed(Math.min(decimals, 7))}`;
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export function useApInvoicesApi() {
  const api = useApi();

  return {
    upload: (file: File): Promise<{ invoice: VendorInvoice; lines: VendorInvoiceLine[] }> => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post('/procurement/vendor-invoices', { body: fd, silent: true });
    },

    /** `status` takes one status or a comma-joined few — the sent-to-books
     *  view fetches 'confirmed,delivered,delivery_failed' as one story. */
    list: (status: string = 'needs_review'): Promise<{ invoices: InvoiceQueueRow[]; delivery_failed_count: number }> =>
      api.get(`/procurement/vendor-invoices?status=${encodeURIComponent(status)}`),

    getOne: (id: string): Promise<InvoiceDetail> =>
      api.get(`/procurement/vendor-invoices/${id}`),

    /** The stored document as a blob for an object URL — the proxy is
     *  Bearer-gated, so a bare <img src> / <iframe src> cannot reach it. */
    fileBlob: async (id: string): Promise<Blob> => {
      const res: Response = await api.get(`/procurement/vendor-invoices/${id}/file`, { skipJsonParse: true });
      if (!res.ok) throw new Error('The stored document could not be fetched.');
      return res.blob();
    },

    /** Path for the forwardable summary, opened through `openAuthedPdf` — the
     *  same helper the PO page's PDF button uses. Not a blob fetch: this one
     *  opens in a tab rather than rendering inside the page. */
    classificationOptions: (): Promise<ClassificationOptions> =>
      api.get('/procurement/vendor-invoices/classification-options'),

    setClassification: (
      id: string,
      body: { purchase_class_id?: number | null; expense_category_id?: string | null; apply_to_all?: boolean },
    ): Promise<{ invoice: VendorInvoice; lines: VendorInvoiceLine[]; lines_touched: number }> =>
      api.patch(`/procurement/vendor-invoices/${id}/classification`, body),

    /** Copy the address this document printed onto the vendor master. Offered
     *  only where the vendor has none; the server refuses to overwrite. */
    adoptVendorAddress: (id: string): Promise<{ vendor: { id: string; name: string } }> =>
      api.post(`/procurement/vendor-invoices/${id}/vendor-address`, {}),

    summaryPdfPath: (id: string): string =>
      `/procurement/vendor-invoices/${id}/summary-pdf`,

    rematch: (id: string): Promise<RematchResult> =>
      api.post(`/procurement/vendor-invoices/${id}/rematch`, { body: {} }),

    updateHeader: (id: string, fields: Partial<Pick<VendorInvoice,
      'vendor_id' | 'invoice_number' | 'invoice_date' | 'due_date' | 'notes'>> & {
      subtotal_cents?: number | null; tax_cents?: number | null; total_cents?: number | null;
    }): Promise<{ invoice: VendorInvoice }> =>
      api.patch(`/procurement/vendor-invoices/${id}`, { body: fields }),

    setLineMatch: (id: string, lineId: string, purchaseOrderLineId: string | null): Promise<{ line: VendorInvoiceLine }> =>
      api.put(`/procurement/vendor-invoices/${id}/lines/${lineId}/match`, {
        body: { purchase_order_line_id: purchaseOrderLineId },
      }),

    /** The gate: rolls billing onto the matched orders, appends the invoiced
     *  event, and stamps the send decision (delivery_intended). The forward
     *  itself happens after commit, server-side; this call never waits on mail.
     *  `send` omitted lets the server default: on, or off for card-paid. */
    confirm: (id: string, send?: boolean): Promise<{ invoice: VendorInvoice; affected_order_ids: string[] }> =>
      api.post(`/procurement/vendor-invoices/${id}/confirm`, {
        body: typeof send === 'boolean' ? { send } : {},
      }),

    /** The manual delivery door: first send when the wake-up was lost, or a
     *  retry after delivery_failed. A skip is the environment declining, not
     *  the invoice failing. */
    deliver: (id: string): Promise<DeliverResult> =>
      api.post(`/procurement/vendor-invoices/${id}/deliver`, { body: {} }),

    /** The PO page's billing read: every invoice billing that order, plus each
     *  line's three-way numbers. Financial-gated like the rest of this module. */
    listForOrder: (orderId: string): Promise<OrderBilling> =>
      api.get(`/procurement/vendor-invoices/for-order/${orderId}`),

    /** The bookkeeper's note: Open / Partial / Paid, the do-not-pay hold with
     *  its reason, and the due date. Confirmed records only — the server
     *  refuses it anywhere else (invoice_not_payable). */
    setPaymentState: (id: string, fields: {
      payment_status?: PaymentStatus;
      payment_on_hold?: boolean;
      payment_hold_reason?: string | null;
      due_date?: string | null;
    }): Promise<{ invoice: VendorInvoice }> =>
      api.patch(`/procurement/vendor-invoices/${id}/payment`, { body: fields }),

    /** Tombstone a confirmed record: rollup self-heals, the PO feed says why. */
    voidInvoice: (id: string, reason: string): Promise<{ invoice: VendorInvoice }> =>
      api.post(`/procurement/vendor-invoices/${id}/void`, { body: { reason } }),

    discard: (id: string, reason: string): Promise<{ invoice: VendorInvoice }> =>
      api.post(`/procurement/vendor-invoices/${id}/discard`, { body: { reason } }),
  };
}
